/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file Summary of who nominated the app and when  (used by the Nominated Apps widget)
 * @module Epic.AppOrchard.Dashboard.NominationInfo
 */

import { StarIcon } from "@chakra-ui/icons";
import { Box, TagProps } from "@chakra-ui/react";
import { WidgetTag } from "ao/components/Dashboard/widgetHelpers";
import { INomination, IUserSecurity } from "ao/types";
import React, { FC, memo } from "react";

interface IProps {
	latestAppVersionId: number;
	nominations: INomination[];
	userSecurity: IUserSecurity;
}

type Props = IProps & TagProps;

/** Summary of who nominated the app and when  (used by the Nominated Apps widget)  */
export const NominationInfo: FC<Props> = memo((props: Props) => {
	const { nominations, userSecurity, latestAppVersionId, ...rest } = props;
	const showUserName =
		(userSecurity.canPurchase || userSecurity.isEpicUser) && nominations.some((n) => n.userName);

	if (nominations.length === 0) {
		return null;
	}

	const nominationSummary = !showUserName // if current user can use name of user who nominated, don't show hover text
		? null
		: nominations.map((nom, index) => {
				const isPriorVersion = latestAppVersionId !== nom.appVersionId;
				return (
					<Box key={index} mt={index === 0 ? "" : "0.5em"}>
						{nom.userName + " nominated"} on {nom.instant.toLocaleDateString()}{" "}
						{isPriorVersion && (
							<Box as="span" fontStyle="italic" fontSize="0.8em">
								(previous app version)
							</Box>
						)}
					</Box>
				);
		  });

	return (
		<>
			<WidgetTag
				label={nominations.length + " nomination" + (nominations.length === 1 ? "" : "s")}
				tooltipContent={nominationSummary}
				icon={StarIcon}
				tooltipProps={{ w: "350px", maxW: "unset" }}
				{...rest}
			></WidgetTag>
		</>
	);
});
